import { find, merge, orderBy } from 'lodash-es'

import { DealerAPI, DealerOverviewAPI, KpiAPI } from 'src/api'
import $store from 'src/store'

const overviewModule = {
  namespaced: true,
  state: {
    defaultPageSettings: {
      /*
      Base object of pageSettings, contains required properties for page.
      Created to prevent difference between vuex and localhost obj after code change
      */
      orderBy: '',
      dealerships: [],
      comparisonMetrics: [],
      comparisonPercentages: [],
    },

    favoriteMetrics: [],
    allMetrics: [],

    pageSettings: JSON.parse(localStorage.getItem('overviewPageSettings')) || {},
  },
  mutations: {
    CLEAR_STORE(state) {
      state.favoriteMetrics = []
      state.allMetrics = []
    },

    ADD_FAVORITE_METRIC(state, payload) {
      state.favoriteMetrics = [...state.favoriteMetrics, payload]
    },
    REMOVE_FAVORITE_METRIC(state, payload) {
      state.favoriteMetrics = state.favoriteMetrics.filter((e) => e !== payload)
    },
    SET_FAVORITE_METRICS(state, payload) {
      state.favoriteMetrics = payload
    },
    SET_ALL_METRICS(state, payload) {
      state.allMetrics = payload
    },
    SET_PAGE_DEALERSHIPS(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].dealerships = payload

      localStorage.setItem('overviewPageSettings', JSON.stringify(state.pageSettings))
    },
    ADD_PAGE_DEALERSHIP(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].dealerships = [
        ...state.pageSettings[$store.state.settings.dealerGroupId].dealerships,
        payload,
      ]

      localStorage.setItem('overviewPageSettings', JSON.stringify(state.pageSettings))
    },
    REMOVE_PAGE_DEALERSHIP(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].dealerships = state.pageSettings[
        $store.state.settings.dealerGroupId
      ].dealerships.filter((e) => e !== payload)

      localStorage.setItem('overviewPageSettings', JSON.stringify(state.pageSettings))
    },
    ADD_PAGE_PERCENTAGE(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].comparisonPercentages.push(payload)

      localStorage.setItem('overviewPageSettings', JSON.stringify(state.pageSettings))
    },
    REMOVE_PAGE_PERCENTAGE(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].comparisonPercentages =
        state.pageSettings[$store.state.settings.dealerGroupId].comparisonPercentages.filter(
          (e) => e !== payload
        )

      localStorage.setItem('overviewPageSettings', JSON.stringify(state.pageSettings))
    },
    SET_PAGE_ORDERBY(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].orderBy = payload

      localStorage.setItem('overviewPageSettings', JSON.stringify(state.pageSettings))
    },
    SET_PAGE_SETTINGS(state, payload) {
      localStorage.setItem(
        'overviewPageSettings',
        JSON.stringify({
          ...state.pageSettings,
          [$store.state.settings.dealerGroupId]: payload,
        })
      )

      state.pageSettings = JSON.parse(localStorage.getItem('overviewPageSettings'))
    },
    ADD_COMPARISON(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].comparisonMetrics.push(payload)

      localStorage.setItem('overviewPageSettings', JSON.stringify(state.pageSettings))
    },
    REMOVE_COMPARISON(state, payload) {
      state.pageSettings[$store.state.settings.dealerGroupId].comparisonMetrics =
        state.pageSettings[$store.state.settings.dealerGroupId].comparisonMetrics.filter(
          (e) => e !== payload
        )

      localStorage.setItem('overviewPageSettings', JSON.stringify(state.pageSettings))
    },
  },
  getters: {
    dealerSettings(state) {
      return state.pageSettings[$store.state.settings.dealerGroupId]
    },
  },
  actions: {
    async getMetrics({ commit }) {
      const list = await KpiAPI.getList()
      const favorite = await KpiAPI.getMy({ type: 1 })

      commit('SET_ALL_METRICS', list)
      commit('SET_FAVORITE_METRICS', favorite.ungrouped)

      return true
    },
    async getMetricsData(
      { rootState, state },
      {
        metric, // Metric, which data must be downloaded
        dealerships = null, // If passed, download metric data only for this dealerships
        lastMonth = false, // lastMonth/lastQuarter metric data for comparison
        lastYear = false, // lastYear metric data for comparison
      }
    ) {
      const { dealerGroupId, viewType } = rootState.settings

      let period

      switch (
        true // Calculating period
      ) {
        case lastYear:
          period = viewType === 'monthsOfYear' ? 'previous' : 'lastYear'
          break
        case lastMonth:
          period = 'previous'
          break
        default:
          period = 'current'
          break
      }

      const data = await KpiAPI.getEnterprise({
        metric,
        dealerships: dealerships || state.pageSettings[dealerGroupId].dealerships,
        period,
      })

      return data
    },
    addFavoriteMetric({ commit }, metric) {
      commit('ADD_FAVORITE_METRIC', metric)
    },
    removeFavoriteMetric({ commit }, metric) {
      commit('REMOVE_FAVORITE_METRIC', metric)
    },
    async restoreMetrics({ commit }) {
      const data = await KpiAPI.restoreDefaults({ type: 1 })

      commit('SET_FAVORITE_METRICS', data.favorites.ungrouped)

      return data
    },
    async setDefaultPageSettings({ commit, rootGetters, rootState, state }) {
      const settings = JSON.parse(JSON.stringify(state.defaultPageSettings))

      if (rootGetters['dealer/dealerships'].length !== 0) {
        settings.dealerships = rootGetters['dealer/currentDealerships']
      } else {
        const data = await DealerAPI.getList()

        const dealer = find(data, {
          id: +rootState.settings.dealerGroupId,
        })

        settings.dealerships = dealer.dealerships.map((e) => e.id)
      }

      commit('SET_PAGE_SETTINGS', settings)
    },
    async exportReport({ state }, body) {
      const { favoriteMetrics } = state
      const data = await DealerOverviewAPI.export({
        metrics: favoriteMetrics,
        ...body,
      })

      return data
    },
    orderByMetric(
      { state, commit, rootState, getters, rootGetters },
      {
        metric, // (String)
        metricsData, // Metric data to calculate dealerships order (Obj)
        reset = false, // If true, resetting current ordering and returns to default ordering (Bool)
        initOrdering = false, // If true, just arrages dealerships with current orderBy metric from state (Bool)
      }
    ) {
      const { dealerGroupId } = rootState.settings
      if (reset) {
        commit(
          'SET_PAGE_ORDERBY',
          getters.dealerSettings.orderBy === 'dealership' ? 'alphabet' : 'dealership'
        )

        const dealerships =
          getters.dealerSettings.orderBy === 'alphabet'
            ? $store.getters['dealer/dealerships']
                .map((e) => ({ id: e.id, name: e.name }))
                .sort((a, b) => {
                  if (a.name < b.name) {
                    return -1
                  }
                  if (a.name > b.name) {
                    return 1
                  }
                  return 0
                })
                .map((e) => +e.id)
                .filter((id) =>
                  +rootState.settings.dealerShipId === 0
                    ? true
                    : rootGetters['dealer/currentDealerships'].includes(id)
                )
            : rootGetters['dealer/currentDealerships']

        commit('SET_PAGE_DEALERSHIPS', dealerships)

        return
      }

      const visibleMetricsData = metricsData.dealerships.filter((e) =>
        state.pageSettings[dealerGroupId].dealerships.includes(e.dealerShipId)
      ) // Filtering extra data (passed metricsData obj may content metrics for hidden dealerships)

      /* Ordering received metricsData by descending */
      const settingsOrderBy = state.pageSettings[dealerGroupId].orderBy
      let sortedDealerships = orderBy(visibleMetricsData, ['value'])

      if (!initOrdering) {
        commit(
          // Replacing orderBy with new metric
          'SET_PAGE_ORDERBY',
          metric === settingsOrderBy && settingsOrderBy[0] !== '-' && settingsOrderBy !== ''
            ? `-${metric}`
            : metric
        )
      }

      /* If orderBy metric contains "-" symbol at the beginning, sort by ascending, else sort by descending */
      if (state.pageSettings[dealerGroupId].orderBy[0] !== '-') {
        sortedDealerships = sortedDealerships.reverse()
      }

      commit(
        'SET_PAGE_DEALERSHIPS',
        sortedDealerships
          .map((e) => e.dealerShipId)
          .filter((id) =>
            +rootState.settings.dealerShipId === 0
              ? true
              : rootGetters['dealer/currentDealerships'].includes(id)
          )
      )
    },
  },
}

export default merge({
  ...overviewModule,
})
